import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress, Chip,
  Button, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, TextField, MenuItem, Divider
} from '@mui/material';
import {
  Home, Add, Edit, Delete, PostAdd, ShoppingCart, Send,
  LockOpen, MoveToInbox, ReceiptLong, Print, Email
} from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { fmt, sym } from '../utils/currency';
import { useLocations } from '../hooks/useLocations';

const STATUS_COLOR = {
  Open: 'primary', Released: 'info', 'Partially Received': 'warning',
  Received: 'secondary', 'Partially Invoiced': 'warning', Invoiced: 'success',
  Posted: 'success', Cancelled: 'default'
};
const APPROVAL_COLOR = { 'Pending Approval': 'warning', Approved: 'success', Rejected: 'error' };
const today = () => new Date().toISOString().split('T')[0];

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const PurchaseOrderList = () => {
  const navigate = useNavigate();
  const [orders,      setOrders]      = useState([]);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [loading,     setLoading]     = useState(true);
  const [viewOrder,   setViewOrder]   = useState(null);
  const [postDialog,  setPostDialog]  = useState(null);
  const [postForm,    setPostForm]    = useState({ supplierInvoiceNo: '', invoiceDate: today() });
  const [cancelOrder, setCancelOrder] = useState(null);
  const [workflowOn,  setWorkflowOn]  = useState(false);
  const [sendOrder,   setSendOrder]   = useState(null);   // order pending "send for approval"
  const [releaseOrder, setReleaseOrder] = useState(null);
  const [receiveOrder, setReceiveOrder] = useState(null);
  const [receiveDate,  setReceiveDate]  = useState(today());
  const [receiveLocation, setReceiveLocation] = useState('');
  const { locations } = useLocations();
  const orderHasItems = (o) => (o?.lines || []).some(l => l.lineType === 'Item' && l.itemNo);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [invoiceForm,  setInvoiceForm]  = useState({ supplierInvoiceNo: '', invoiceDate: today() });
  const [emailOrder,   setEmailOrder]   = useState(null);
  const [emailForm,    setEmailForm]    = useState({ to: '', message: '' });
  const [busy,         setBusy]         = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/payables/orders`, { headers });
      setOrders(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []); // eslint-disable-line

  // Is the approval workflow switched on for Purchase Orders?
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/approvals/enabled/PurchaseOrder`, { headers })
      .then(r => setWorkflowOn(!!r.data?.enabled))
      .catch(() => {});
  }, []); // eslint-disable-line

  const handleSend = async () => {
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/api/approvals/send`,
        { documentType: 'PurchaseOrder', documentId: sendOrder._id }, { headers });
      setSendOrder(null);
      setSuccess(data.autoApproved ? 'Approved — ready to post' : 'Sent for approval');
      fetchOrders();
    } catch (err) {
      setSendOrder(null);
      setError(err.response?.data?.error || 'Failed to send for approval');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/payables/orders/${cancelOrder}`, { headers });
      setCancelOrder(null);
      setSuccess('Order cancelled');
      fetchOrders();
    } catch (err) {
      setCancelOrder(null);
      setError(err.response?.data?.error || 'Failed to cancel order');
    }
  };

  const handlePost = async () => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payables/orders/${postDialog.orderNo}/post`,
        postForm,
        { headers }
      );
      setPostDialog(null);
      setSuccess(data.message || 'Purchase Invoice created successfully');
      fetchOrders();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post order');
      setPostDialog(null);
    }
  };

  // Generic lifecycle action runner (release / receive / invoice).
  const runAction = async (orderNo, action, body, onDone) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payables/orders/${orderNo}/${action}`,
        body || {}, { headers }
      );
      onDone && onDone();
      setSuccess(data.message || 'Done');
      fetchOrders();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} order`);
      onDone && onDone();
    } finally {
      setBusy(false);
    }
  };

  const handleRelease = () => runAction(releaseOrder.orderNo, 'release', {}, () => setReleaseOrder(null));
  const handleReceive = () => runAction(receiveOrder.orderNo, 'receive', { receiptDate: receiveDate, locationCode: receiveLocation }, () => setReceiveOrder(null));
  const handleInvoice = () => runAction(invoiceOrder.orderNo, 'invoice', invoiceForm, () => setInvoiceOrder(null));

  const handleEmail = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payables/orders/${emailOrder.orderNo}/send-email`,
        emailForm, { headers }
      );
      setEmailOrder(null);
      setSuccess(data.message || 'Email sent');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setBusy(false);
    }
  };

  // Progress helpers for the row (received/invoiced against ordered qty)
  const lineTotals = (o) => {
    const lines = o.lines || [];
    const items = lines.filter(l => l.lineType === 'Item');
    const recv  = items.reduce((s, l) => s + (l.qtyReceived || 0), 0);
    const recvT = items.reduce((s, l) => s + (l.qty || 0), 0);
    const inv   = lines.reduce((s, l) => s + (l.qtyInvoiced || 0), 0);
    const invT  = lines.reduce((s, l) => s + (l.qty || 0), 0);
    return { hasItems: items.length > 0, recv, recvT, inv, invT };
  };

  const canReceive = (s) => ['Released', 'Partially Received'].includes(s);
  const canInvoice = (s) => ['Released', 'Partially Received', 'Received', 'Partially Invoiced'].includes(s);

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading orders...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <ShoppingCart sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Purchase Orders</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/payables')} title="Payables Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={() => navigate('/payables/orders/new')} title="New Order"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>
          <Toast error={error} success={success} onClose={(k) => (k === 'error' ? setError('') : setSuccess(''))} />

          {orders.length === 0 ? <Typography>No purchase orders found.</Typography> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Order No</strong></TableCell>
                  <TableCell><strong>Supplier</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Expected</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell align="center"><strong>Lines</strong></TableCell>
                  <TableCell align="right"><strong>Total ({sym()})</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map(o => (
                  <TableRow key={o.orderNo} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewOrder(o)}>
                    <TableCell><strong>{o.orderNo}</strong></TableCell>
                    <TableCell>{o.supplierCode}</TableCell>
                    <TableCell>{o.orderDate ? new Date(o.orderDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell>{o.expectedDate ? new Date(o.expectedDate).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>
                      {o.locationCode
                        ? <Chip label={o.locationCode} size="small" variant="outlined" />
                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                      <Chip label={o.lines?.length || 0} size="small" color="info"
                        variant="outlined" onClick={() => setViewOrder(o)}
                        title="Click to view line items"
                        sx={{ cursor: 'pointer', fontWeight: 700, minWidth: 40 }} />
                    </TableCell>
                    <TableCell align="right">{(o.totalAmount || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                        <Chip label={o.status} color={STATUS_COLOR[o.status] || 'default'}
                          size="small" variant="outlined" />
                        {workflowOn && o.approvalStatus && o.approvalStatus !== 'None' && (
                          <Chip label={o.approvalStatus} color={APPROVAL_COLOR[o.approvalStatus] || 'default'}
                            size="small" />
                        )}
                        {(() => {
                          const t = lineTotals(o);
                          return (
                            <>
                              {t.hasItems && t.recv > 0 && (
                                <Chip label={`Recv ${t.recv}/${t.recvT}`} size="small" variant="outlined"
                                  color="secondary" sx={{ fontSize: '0.65rem', height: 18 }} />
                              )}
                              {t.inv > 0 && (
                                <Chip label={`Inv ${t.inv}/${t.invT}`} size="small" variant="outlined"
                                  color="success" sx={{ fontSize: '0.65rem', height: 18 }} />
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Box display="flex" alignItems="center">
                        {o.status === 'Open' && (
                          <IconButton color="primary" size="small"
                            onClick={() => navigate(`/payables/orders/${o.orderNo}/edit`)} title="Edit">
                            <Edit fontSize="small" />
                          </IconButton>
                        )}

                        {/* Open: approval-aware Release / Send for Approval */}
                        {o.status === 'Open' && (
                          workflowOn && (o.approvalStatus === 'None' || o.approvalStatus === 'Rejected') ? (
                            <IconButton color="warning" size="small"
                              onClick={() => setSendOrder(o)} title="Send for Approval">
                              <Send fontSize="small" />
                            </IconButton>
                          ) : (!workflowOn || o.approvalStatus === 'Approved') ? (
                            <IconButton color="info" size="small"
                              onClick={() => setReleaseOrder(o)} title="Release">
                              <LockOpen fontSize="small" />
                            </IconButton>
                          ) : null
                        )}

                        {/* Receive */}
                        {canReceive(o.status) && (
                          <IconButton color="secondary" size="small"
                            onClick={() => { setReceiveOrder(o); setReceiveDate(today()); setReceiveLocation(o.locationCode || ''); }} title="Receive Goods">
                            <MoveToInbox fontSize="small" />
                          </IconButton>
                        )}

                        {/* Invoice */}
                        {canInvoice(o.status) && (
                          <IconButton color="success" size="small"
                            onClick={() => { setInvoiceOrder(o); setInvoiceForm({ supplierInvoiceNo: '', invoiceDate: today() }); }}
                            title="Invoice">
                            <ReceiptLong fontSize="small" />
                          </IconButton>
                        )}

                        {/* Post = Receive & Invoice in one step */}
                        {canReceive(o.status) && (
                          <IconButton color="success" size="small"
                            onClick={() => { setPostDialog(o); setPostForm({ supplierInvoiceNo: '', invoiceDate: today(), locationCode: o.locationCode || '' }); }}
                            title="Post (Receive & Invoice)">
                            <PostAdd fontSize="small" />
                          </IconButton>
                        )}

                        {/* Print + Email — always available */}
                        <IconButton size="small"
                          onClick={() => navigate(`/payables/orders/${o.orderNo}/print`)} title="Print">
                          <Print fontSize="small" />
                        </IconButton>
                        {o.status !== 'Cancelled' && (
                          <IconButton size="small"
                            onClick={() => { setEmailOrder(o); setEmailForm({ to: '', message: '' }); }} title="Email to Vendor">
                            <Email fontSize="small" />
                          </IconButton>
                        )}

                        {/* Cancel (Open/Released only) */}
                        {['Open', 'Released'].includes(o.status) && (
                          <IconButton color="error" size="small"
                            onClick={() => setCancelOrder(o.orderNo)} title="Cancel">
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Order Card (view) ── */}
      <Dialog open={Boolean(viewOrder)} onClose={() => setViewOrder(null)} maxWidth="md" fullWidth>
        {viewOrder && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <ShoppingCart color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewOrder.orderNo}</Typography>
                  <Typography variant="caption" color="text.secondary">Purchase Order</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewOrder.status} color={STATUS_COLOR[viewOrder.status] || 'default'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center',
              px: 3, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
              {viewOrder.status === 'Open' && (
                <Button size="small" color="primary" startIcon={<Edit />}
                  onClick={() => { setViewOrder(null); navigate(`/payables/orders/${viewOrder.orderNo}/edit`); }}>
                  Edit
                </Button>
              )}
              {viewOrder.status === 'Open' && (
                workflowOn && (viewOrder.approvalStatus === 'None' || viewOrder.approvalStatus === 'Rejected') ? (
                  <Button size="small" color="warning" variant="outlined" startIcon={<Send />}
                    onClick={() => { const o = viewOrder; setViewOrder(null); setSendOrder(o); }}>
                    Send for Approval
                  </Button>
                ) : (!workflowOn || viewOrder.approvalStatus === 'Approved') ? (
                  <Button size="small" color="info" variant="outlined" startIcon={<LockOpen />}
                    onClick={() => { const o = viewOrder; setViewOrder(null); setReleaseOrder(o); }}>
                    Release
                  </Button>
                ) : null
              )}
              {canReceive(viewOrder.status) && (
                <Button size="small" color="secondary" variant="outlined" startIcon={<MoveToInbox />}
                  onClick={() => { const o = viewOrder; setViewOrder(null); setReceiveOrder(o); setReceiveDate(today()); setReceiveLocation(o.locationCode || ''); }}>
                  Receive
                </Button>
              )}
              {canInvoice(viewOrder.status) && (
                <Button size="small" color="success" variant="outlined" startIcon={<ReceiptLong />}
                  onClick={() => { const o = viewOrder; setViewOrder(null); setInvoiceOrder(o); setInvoiceForm({ supplierInvoiceNo: '', invoiceDate: today() }); }}>
                  Invoice
                </Button>
              )}
              {canReceive(viewOrder.status) && (
                <Button size="small" color="success" variant="contained" startIcon={<PostAdd />}
                  onClick={() => { const o = viewOrder; setViewOrder(null); setPostDialog(o); setPostForm({ supplierInvoiceNo: '', invoiceDate: today(), locationCode: o.locationCode || '' }); }}>
                  Post
                </Button>
              )}
              <Button size="small" startIcon={<Print />}
                onClick={() => { setViewOrder(null); navigate(`/payables/orders/${viewOrder.orderNo}/print`); }}>
                Print
              </Button>
              {viewOrder.status !== 'Cancelled' && (
                <Button size="small" startIcon={<Email />}
                  onClick={() => { const o = viewOrder; setViewOrder(null); setEmailOrder(o); setEmailForm({ to: '', message: '' }); }}>
                  Email
                </Button>
              )}
              {['Open', 'Released'].includes(viewOrder.status) && (
                <Button size="small" color="error" startIcon={<Delete />}
                  onClick={() => { setViewOrder(null); setCancelOrder(viewOrder.orderNo); }}>
                  Cancel
                </Button>
              )}
            </Box>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Document</Typography>
              <InfoRow label="Order No"       value={viewOrder.orderNo} />
              <InfoRow label="Order Date"     value={viewOrder.orderDate ? new Date(viewOrder.orderDate).toLocaleDateString() : ''} />
              <InfoRow label="Expected Date"  value={viewOrder.expectedDate ? new Date(viewOrder.expectedDate).toLocaleDateString() : ''} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Supplier</Typography>
              <InfoRow label="Supplier Code"  value={viewOrder.supplierCode} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Receiving</Typography>
              <InfoRow label="Receive-to Location" value={viewOrder.locationCode} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>
                Line Items ({viewOrder.lines?.length || 0})
              </Typography>
              {viewOrder.lines && viewOrder.lines.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>Item / FA / GL</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, py: 0.8 }}>Qty</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, py: 0.8 }}>Recv / Inv</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, py: 0.8 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, py: 0.8 }}>VAT</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, py: 0.8 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewOrder.lines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ py: 0.6 }}>{line.lineNo || idx + 1}</TableCell>
                        <TableCell sx={{ py: 0.6 }}>{line.lineType}</TableCell>
                        <TableCell sx={{ py: 0.6 }}>{line.itemNo || line.faNo || line.glAccount || '—'}</TableCell>
                        <TableCell sx={{ py: 0.6, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={line.description}>{line.description}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6 }}>{line.qty}</TableCell>
                        <TableCell align="center" sx={{ py: 0.6, fontSize: '0.72rem', color: 'text.secondary' }}>
                          {line.lineType === 'Item' ? (line.qtyReceived || 0) : '—'} / {line.qtyInvoiced || 0}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.6 }}>{fmt(line.unitPrice)}</TableCell>
                        <TableCell align="right" sx={{ py: 0.6 }}
                          title={line.vatPercent ? `${line.vatPercent}%` : ''}>
                          {line.vatAmount ? fmt(line.vatAmount) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.6, fontWeight: 600 }}>{fmt(line.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No lines on this order.</Typography>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Totals</Typography>
              <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.3}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal: {sym()} {fmt(viewOrder.subtotal ?? (viewOrder.lines || []).reduce((s, l) => s + (l.amount || 0), 0))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  VAT: {sym()} {fmt(viewOrder.totalVAT || 0)}
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  Total: {sym()} {fmt(viewOrder.totalAmount || 0)}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setViewOrder(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Post dialog (Receive & Invoice in one step) */}
      <Dialog open={Boolean(postDialog)} onClose={() => setPostDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Post Order: {postDialog?.orderNo}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              This will <strong>receive</strong> any outstanding items and <strong>invoice</strong> the order
              ({sym()} {fmt(postDialog?.totalAmount)}) in one step.
            </Typography>
            {orderHasItems(postDialog) && (
              <TextField select label="Receive-to Location" value={postForm.locationCode || ''}
                onChange={e => setPostForm({ ...postForm, locationCode: e.target.value })}
                fullWidth size="small" required
                helperText={postDialog?.locationCode
                  ? 'From the order — change it here if needed'
                  : 'This order has no location (e.g. from a quote) — pick one'}>
                <MenuItem value=""><em>Select Location</em></MenuItem>
                {locations.map(loc => (
                  <MenuItem key={loc.locationCode} value={loc.locationCode}>
                    {loc.locationCode} – {loc.locationName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField label="Supplier Invoice No (optional)" value={postForm.supplierInvoiceNo}
              onChange={e => setPostForm({ ...postForm, supplierInvoiceNo: e.target.value })}
              fullWidth size="small" />
            <TextField label="Invoice Date" type="date" value={postForm.invoiceDate}
              onChange={e => setPostForm({ ...postForm, invoiceDate: e.target.value })}
              fullWidth size="small" InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handlePost} disabled={busy}>
            {busy ? 'Posting…' : 'Receive & Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release dialog */}
      <Dialog open={Boolean(releaseOrder)} onClose={() => setReleaseOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Release Order {releaseOrder?.orderNo}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Releasing marks the order ready for receiving and invoicing. It can no longer be edited
            once released.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseOrder(null)}>Cancel</Button>
          <Button variant="contained" color="info" onClick={handleRelease} disabled={busy}>
            {busy ? 'Releasing…' : 'Release'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive dialog */}
      <Dialog open={Boolean(receiveOrder)} onClose={() => setReceiveOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Receive Goods: {receiveOrder?.orderNo}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Posts a goods receipt for the outstanding item lines into stock. No GL is posted
              until you invoice.
            </Typography>
            {orderHasItems(receiveOrder) && (
              <TextField select label="Receive-to Location" value={receiveLocation}
                onChange={e => setReceiveLocation(e.target.value)}
                fullWidth size="small" required
                helperText={receiveOrder?.locationCode
                  ? 'From the order — change it here if needed'
                  : 'This order has no location (e.g. from a quote) — pick one'}>
                <MenuItem value=""><em>Select Location</em></MenuItem>
                {locations.map(loc => (
                  <MenuItem key={loc.locationCode} value={loc.locationCode}>
                    {loc.locationCode} – {loc.locationName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField label="Receipt Date" type="date" value={receiveDate}
              onChange={e => setReceiveDate(e.target.value)}
              fullWidth size="small" InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveOrder(null)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={handleReceive} disabled={busy}>
            {busy ? 'Receiving…' : 'Post Receipt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice dialog */}
      <Dialog open={Boolean(invoiceOrder)} onClose={() => setInvoiceOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Invoice Order: {invoiceOrder?.orderNo}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Posts a Purchase Invoice for received item lines and any service/GL/FA lines
              (GL + Input VAT + AP).
            </Typography>
            <TextField label="Supplier Invoice No (optional)" value={invoiceForm.supplierInvoiceNo}
              onChange={e => setInvoiceForm({ ...invoiceForm, supplierInvoiceNo: e.target.value })}
              fullWidth size="small" />
            <TextField label="Invoice Date" type="date" value={invoiceForm.invoiceDate}
              onChange={e => setInvoiceForm({ ...invoiceForm, invoiceDate: e.target.value })}
              fullWidth size="small" InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceOrder(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleInvoice} disabled={busy}>
            {busy ? 'Posting…' : 'Post Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email to vendor dialog */}
      <Dialog open={Boolean(emailOrder)} onClose={() => setEmailOrder(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Email Purchase Order {emailOrder?.orderNo}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Sends the order to the vendor on your company letterhead. Leave the address blank to use
              the supplier's saved email.
            </Typography>
            <TextField label="Recipient Email (optional)" value={emailForm.to} type="email"
              onChange={e => setEmailForm({ ...emailForm, to: e.target.value })}
              placeholder="uses supplier email if blank"
              fullWidth size="small" />
            <TextField label="Message (optional)" value={emailForm.message}
              onChange={e => setEmailForm({ ...emailForm, message: e.target.value })}
              fullWidth size="small" multiline rows={3} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOrder(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<Email />} onClick={handleEmail} disabled={busy}>
            {busy ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send for approval dialog */}
      <Dialog open={Boolean(sendOrder)} onClose={() => setSendOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Send {sendOrder?.orderNo} for approval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This purchase order will be routed to the configured approver(s). You can post it only
            once it has been fully approved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOrder(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleSend}>Send for Approval</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirm dialog */}
      <Dialog open={Boolean(cancelOrder)} onClose={() => setCancelOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Order {cancelOrder}</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to cancel order {cancelOrder}?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOrder(null)}>No</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrderList;
